/**
 * Dialog „Co się stało?" (§8.4) — duże kafle PL + pole tekstowe TYLKO do NPC.
 * Input: czysty DOM (bez Phaser DOM plugin — main.ts WP0 nie montuje dom.createContainer).
 */
import Phaser from 'phaser';
import { COMPANION_INTENTS, type CompanionIntentId } from '@pieskowo/shared';
import { MIN_TOUCH } from '../core/UiKit.js';
import { askCompanion } from './askClient.js';

/** Kafle startowe (min. 6) — etykiety z shared, PL z diakrytykami. */
const TILE_INTENTS: CompanionIntentId[] = [
  'WHERE_TO_GO',
  'HOW_TO_WIN',
  'HOW_TO_EARN',
  'WHERE_BUY',
  'HOW_TO_BUILD',
  'LOST',
];

export type AskDialogResult = {
  responseId: string | null;
  intentId?: string;
  /** Zdanie PL do pokazania w dymku (z serwera lub fallback offline). */
  sentencePl?: string;
};

/** Sensowne zdanie PL, gdy brak sieci / serwer nie odpowiedział. */
const OFFLINE_FALLBACK_SENTENCE_PL =
  'Nie słyszę Cię teraz — spróbujmy zapytać jeszcze raz za chwilę!';

export type AskDialogOpts = {
  scene: Phaser.Scene;
  sceneKey: string;
  onClose: (result: AskDialogResult) => void;
  /** Offline / stub: lokalna odpowiedź bez sieci. */
  offlineResponseId?: string;
  /** Offline / stub: lokalne zdanie PL bez sieci. */
  offlineSentencePl?: string;
};

export class AskDialog {
  private root: Phaser.GameObjects.Container;
  private htmlWrap: HTMLDivElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private closed = false;
  private readonly scene: Phaser.Scene;
  private readonly sceneKey: string;
  private readonly onClose: (result: AskDialogResult) => void;
  private readonly offlineResponseId: string;
  private readonly offlineSentencePl: string;

  constructor(opts: AskDialogOpts) {
    this.scene = opts.scene;
    this.sceneKey = opts.sceneKey;
    this.onClose = opts.onClose;
    this.offlineResponseId = opts.offlineResponseId ?? 'resp_fallback';
    this.offlineSentencePl =
      opts.offlineSentencePl ?? OFFLINE_FALLBACK_SENTENCE_PL;

    const w = 720;
    const h = 480;
    const cx = 0;
    const cy = 0;

    const dim = opts.scene.add
      .rectangle(0, 0, 1280 * 2, 720 * 2, 0x000000, 0.45)
      .setInteractive();

    const panel = opts.scene.add
      .rectangle(cx, cy, w, h, 0xfff8f0)
      .setStrokeStyle(4, 0xffb6c1);

    const title = opts.scene.add
      .text(cx, cy - h / 2 + 36, 'Co się stało?', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const subtitle = opts.scene.add
      .text(cx, cy - h / 2 + 72, 'Wybierz obrazek albo napisz do kotka', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    const tiles: Phaser.GameObjects.GameObject[] = [];
    const cols = 3;
    const tileW = 200;
    const tileH = Math.max(MIN_TOUCH + 12, 76);
    const startX = cx - ((cols - 1) * (tileW + 12)) / 2;
    const startY = cy - 80;

    TILE_INTENTS.forEach((intentId, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (tileW + 12);
      const y = startY + row * (tileH + 14);
      const label = COMPANION_INTENTS[intentId].labelPl;
      const bg = opts.scene.add
        .rectangle(x, y, tileW, tileH, 0xb8e6d0)
        .setStrokeStyle(3, 0xffffff)
        .setInteractive({ useHandCursor: true });
      const txt = opts.scene.add
        .text(x, y, label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: '#4a3f55',
          fontStyle: 'bold',
          align: 'center',
          wordWrap: { width: tileW - 16 },
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => {
        void this.submitIntent(intentId);
      });
      tiles.push(bg, txt);
    });

    const sendBtn = opts.scene.add
      .rectangle(cx + 220, cy + h / 2 - 70, 100, Math.max(MIN_TOUCH, 48), 0xffb6c1)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const sendLabel = opts.scene.add
      .text(cx + 220, cy + h / 2 - 70, 'Wyślij', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    sendBtn.on('pointerdown', () => {
      void this.submitText();
    });

    const closeBtn = opts.scene.add
      .text(cx + w / 2 - 28, cy - h / 2 + 20, '✕', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.close({ responseId: null }));

    this.root = opts.scene.add.container(640, 360, [
      dim,
      panel,
      title,
      subtitle,
      ...tiles,
      sendBtn,
      sendLabel,
      closeBtn,
    ]);
    this.root.setDepth(5000);
    this.root.setScrollFactor(0);

    this.mountDomInput();
  }

  private mountDomInput(): void {
    try {
      const parent =
        (this.scene.game.canvas.parentElement as HTMLElement | null) ??
        document.getElementById('game-container') ??
        document.body;
      const wrap = document.createElement('div');
      wrap.setAttribute('data-companion-ask', '1');
      wrap.style.cssText =
        'position:absolute;left:50%;top:68%;transform:translate(-50%,-50%);' +
        'z-index:9999;display:flex;gap:8px;align-items:center;pointer-events:auto;';
      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 60;
      input.placeholder = 'Napisz do kotka…';
      input.setAttribute('aria-label', 'Napisz do kotka');
      input.style.cssText =
        'width:min(420px,70vw);height:48px;font-size:20px;border-radius:12px;' +
        'border:3px solid #ffb6c1;padding:0 12px;font-family:system-ui,sans-serif;' +
        'color:#4a3f55;background:#fff;';
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          void this.submitText();
        }
      });
      wrap.appendChild(input);
      parent.style.position = parent.style.position || 'relative';
      parent.appendChild(wrap);
      this.htmlWrap = wrap;
      this.inputEl = input;
      input.focus();
    } catch {
      this.htmlWrap = null;
      this.inputEl = null;
    }
  }

  private getInputValue(): string {
    return (this.inputEl?.value ?? '').trim().slice(0, 60);
  }

  private async submitIntent(intentId: CompanionIntentId): Promise<void> {
    if (this.closed) return;
    const res = await askCompanion({ intentId, sceneKey: this.sceneKey });
    this.close({
      responseId: res?.responseId ?? this.offlineResponseId,
      sentencePl: res?.sentencePl ?? this.offlineSentencePl,
      intentId,
    });
  }

  private async submitText(): Promise<void> {
    if (this.closed) return;
    const text = this.getInputValue();
    if (!text) return;
    const res = await askCompanion({ text, sceneKey: this.sceneKey });
    this.close({
      responseId: res?.responseId ?? this.offlineResponseId,
      sentencePl: res?.sentencePl ?? this.offlineSentencePl,
    });
  }

  private close(result: AskDialogResult): void {
    if (this.closed) return;
    this.closed = true;
    if (this.htmlWrap?.parentElement) {
      this.htmlWrap.parentElement.removeChild(this.htmlWrap);
    }
    this.htmlWrap = null;
    this.inputEl = null;
    this.root.destroy(true);
    this.onClose(result);
  }

  destroy(): void {
    this.close({ responseId: null });
  }
}
